import { PlayIcon, Tick02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { timezoneItems } from "@/lib/timezones";

function applyMetaToForm(
  meta: Record<string, unknown>,
  setters: {
    setMetaTitle: (v: string) => void;
    setMetaTags: (v: string) => void;
    setMetaOwner: (v: string) => void;
    setMetaGuide: (v: string) => void;
    setMetaPlayer: (v: string) => void;
    setMetaTimezone: (v: string) => void;
    setMetaSummary: (v: string) => void;
    setMetaIsFree: (v: string) => void;
  },
) {
  if (typeof meta.title === "string") setters.setMetaTitle(meta.title);
  if (Array.isArray(meta.tags)) {
    setters.setMetaTags(
      meta.tags.filter((t): t is string => typeof t === "string").join(", "),
    );
  }
  const cast =
    meta.cast && typeof meta.cast === "object" && !Array.isArray(meta.cast)
      ? (meta.cast as Record<string, unknown>)
      : null;
  if (typeof cast?.protagonist === "string") {
    setters.setMetaOwner(cast.protagonist);
  } else if (typeof meta.phone_owner_id === "string") {
    setters.setMetaOwner(meta.phone_owner_id);
  }
  if (typeof cast?.guide === "string") {
    setters.setMetaGuide(cast.guide);
  } else if (typeof meta.guide_person_id === "string") {
    setters.setMetaGuide(meta.guide_person_id);
  }
  if (typeof cast?.player === "string") {
    setters.setMetaPlayer(cast.player);
  } else if (typeof meta.player_person_id === "string") {
    setters.setMetaPlayer(meta.player_person_id);
  }
  const tzs = meta.timezone_candidates;
  if (Array.isArray(tzs) && typeof tzs[0] === "string") {
    setters.setMetaTimezone(tzs[0]);
  } else if (typeof meta.timezone === "string") {
    setters.setMetaTimezone(meta.timezone);
  }
  if (typeof meta.summary === "string") setters.setMetaSummary(meta.summary);
  if (typeof meta.is_free === "string" && meta.is_free.trim()) {
    setters.setMetaIsFree(meta.is_free.trim());
  }
}

export function metaLooksEmpty(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta) return true;
  const title = typeof meta.title === "string" ? meta.title.trim() : "";
  const cast =
    meta.cast && typeof meta.cast === "object" && !Array.isArray(meta.cast)
      ? (meta.cast as Record<string, unknown>)
      : null;
  const protagonist =
    typeof cast?.protagonist === "string" ? cast.protagonist.trim() : "";
  const summary = typeof meta.summary === "string" ? meta.summary.trim() : "";
  return !title && !protagonist && !summary;
}

export type ScriptMetaRole = "source" | "gate";

export type ScriptMetaEditorProps = {
  caseId: string;
  versionId: string;
  caseDisplayId: string;
  versionStatus?: string | null;
  hasPipeline?: boolean;
  /**
   * source: case-level input library (edit anytime, no start).
   * gate: version onboarding (confirm then start pipeline).
   */
  role?: ScriptMetaRole;
  title?: string;
  description?: string;
  /** Draft meta once when script exists and meta is empty (create flow). */
  autoDraft?: boolean;
  /** Show Start pipeline action when version has not started pipeline yet. */
  showStartPipeline?: boolean;
  /** Optional control rendered next to header actions (e.g. version picker). */
  headerExtra?: ReactNode;
  /** Cross-link to the other surface (Source ↔ Meta). */
  crossLink?: ReactNode;
};

/**
 * Script + meta form shared by case Source and version Meta.
 * Same data; role only changes copy and whether Start pipeline is shown.
 */
export function ScriptMetaEditor({
  caseId,
  versionId,
  caseDisplayId,
  versionStatus = null,
  hasPipeline = false,
  role = "source",
  title,
  description,
  autoDraft = false,
  showStartPipeline = false,
  headerExtra,
  crossLink,
}: ScriptMetaEditorProps) {
  const resolvedTitle =
    title ?? (role === "gate" ? "元信息" : "源文");
  const resolvedDescription =
    description ??
    (role === "gate"
      ? "确认此版本的剧本与元信息，然后启动流水线。"
      : "案件输入库。可随时编辑剧本与元信息；切换版本以对比包内容。");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const reviewQuery = useQuery({
    queryKey: ["script-meta", versionId],
    queryFn: () => api.getScriptMeta(versionId),
    enabled: Boolean(versionId),
  });

  const [scriptEdit, setScriptEdit] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaTags, setMetaTags] = useState("");
  const [metaOwner, setMetaOwner] = useState("");
  const [metaGuide, setMetaGuide] = useState("");
  const [metaPlayer, setMetaPlayer] = useState("");
  const [metaTimezone, setMetaTimezone] = useState("");
  const [metaSummary, setMetaSummary] = useState("");
  const [metaIsFree, setMetaIsFree] = useState("3/8");
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const autoDraftedRef = useRef(false);
  const lastVersionIdRef = useRef(versionId);

  const pipelineStarted =
    versionStatus === "published" ||
    (hasPipeline && versionStatus !== "failed");

  // Reset form hydrate + auto-draft gate when switching versions.
  useEffect(() => {
    if (lastVersionIdRef.current !== versionId) {
      lastVersionIdRef.current = versionId;
      autoDraftedRef.current = false;
      setScriptEdit("");
      setMetaTitle("");
      setMetaTags("");
      setMetaOwner("");
      setMetaGuide("");
      setMetaPlayer("");
      setMetaTimezone("");
      setMetaSummary("");
      setMetaIsFree("3/8");
      setError(null);
    }
  }, [versionId]);

  useEffect(() => {
    const data = reviewQuery.data;
    if (!data || data.versionId !== versionId) return;
    if (typeof data.script === "string") setScriptEdit(data.script);
    const meta = data.meta ?? {};
    setMetaTitle(typeof meta.title === "string" ? meta.title : "");
    setMetaTags(
      Array.isArray(meta.tags)
        ? meta.tags.filter((t): t is string => typeof t === "string").join(", ")
        : "",
    );
    const cast =
      meta.cast && typeof meta.cast === "object" && !Array.isArray(meta.cast)
        ? (meta.cast as Record<string, unknown>)
        : null;
    setMetaOwner(
      typeof cast?.protagonist === "string"
        ? cast.protagonist
        : typeof meta.phone_owner_id === "string"
          ? meta.phone_owner_id
          : "",
    );
    setMetaGuide(
      typeof cast?.guide === "string"
        ? cast.guide
        : typeof meta.guide_person_id === "string"
          ? meta.guide_person_id
          : "",
    );
    setMetaPlayer(
      typeof cast?.player === "string"
        ? cast.player
        : typeof meta.player_person_id === "string"
          ? meta.player_person_id
          : "",
    );
    const tzs = meta.timezone_candidates;
    setMetaTimezone(
      Array.isArray(tzs) && typeof tzs[0] === "string"
        ? tzs[0]
        : typeof meta.timezone === "string"
          ? meta.timezone
          : "",
    );
    setMetaSummary(typeof meta.summary === "string" ? meta.summary : "");
    setMetaIsFree(
      typeof meta.is_free === "string" && meta.is_free.trim()
        ? meta.is_free.trim()
        : "3/8",
    );
  }, [reviewQuery.data, versionId]);

  useEffect(() => {
    if (!autoDraft) return;
    if (autoDraftedRef.current) return;
    if (genBusy) return;
    if (reviewQuery.isLoading || !reviewQuery.data) return;
    if (reviewQuery.data.versionId !== versionId) return;
    const script =
      typeof reviewQuery.data.script === "string"
        ? reviewQuery.data.script.trim()
        : "";
    if (!script) return;
    if (!metaLooksEmpty(reviewQuery.data.meta)) return;

    autoDraftedRef.current = true;
    setGenBusy(true);
    setError(null);
    void (async () => {
      try {
        const { meta } = await api.generateMeta(versionId, { script });
        applyMetaToForm(meta, {
          setMetaTitle,
          setMetaTags,
          setMetaOwner,
          setMetaGuide,
          setMetaPlayer,
          setMetaTimezone,
          setMetaSummary,
          setMetaIsFree,
        });
        await api.saveScriptMeta(versionId, { script, meta });
        await queryClient.invalidateQueries({
          queryKey: ["script-meta", versionId],
        });
        await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
        toast.add({
          type: "success",
          title: "元信息已草拟",
          description: "请核对字段，然后保存或启动流水线。",
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.code : "meta_generate_failed");
        autoDraftedRef.current = false;
      } finally {
        setGenBusy(false);
      }
    })();
  }, [
    autoDraft,
    reviewQuery.isLoading,
    reviewQuery.data,
    genBusy,
    versionId,
    caseId,
    queryClient,
  ]);

  const buildMeta = (): Record<string, unknown> => {
    const tags = metaTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    return {
      id: caseDisplayId,
      title: metaTitle.trim() || caseDisplayId,
      tags,
      cast: {
        protagonist: metaOwner.trim() || "Alex",
        guide: metaGuide.trim() || "Jen",
        player: metaPlayer.trim() || "Sam",
      },
      timezone_candidates: metaTimezone.trim()
        ? [metaTimezone.trim()]
        : ["America/Los_Angeles"],
      summary: metaSummary.trim(),
      is_free: metaIsFree.trim() || "3/8",
    };
  };

  const onSave = async () => {
    if (busy || genBusy) return;
    setBusy(true);
    setError(null);
    try {
      await api.saveScriptMeta(versionId, {
        script: scriptEdit,
        meta: buildMeta(),
      });
      toast.add({
        type: "success",
        title: "已保存",
        description: "剧本与元信息已保存。",
      });
      await queryClient.invalidateQueries({ queryKey: ["script-meta", versionId] });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "save_failed");
    } finally {
      setBusy(false);
    }
  };

  const onGenerate = async () => {
    if (genBusy || !scriptEdit.trim()) return;
    setGenBusy(true);
    setError(null);
    try {
      const { meta } = await api.generateMeta(versionId, {
        script: scriptEdit,
      });
      applyMetaToForm(meta, {
        setMetaTitle,
        setMetaTags,
        setMetaOwner,
        setMetaGuide,
        setMetaPlayer,
        setMetaTimezone,
        setMetaSummary,
        setMetaIsFree,
      });
      toast.add({
        type: "success",
        title: "元信息已草拟",
        description: "请核对字段，然后保存。",
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "meta_generate_failed");
    } finally {
      setGenBusy(false);
    }
  };

  const onStart = async () => {
    if (busy || pipelineStarted) return;
    setBusy(true);
    setError(null);
    try {
      await api.startPipeline(versionId, {
        script: scriptEdit,
        meta: buildMeta(),
      });
      toast.add({
        type: "success",
        title: "流水线已启动",
        description:
          "generate_phone → refine_phone → experience_guide → assets → review → enrich",
      });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      await queryClient.invalidateQueries({
        queryKey: ["pipeline-runs", versionId],
      });
      await navigate({
        to: "/cases/$caseId/versions/$versionId/pipeline",
        params: { caseId, versionId },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "pipeline_start_failed");
    } finally {
      setBusy(false);
    }
  };

  const tzItems = timezoneItems(metaTimezone);
  const fieldsLocked = genBusy;
  const canStart =
    showStartPipeline &&
    Boolean(scriptEdit.trim()) &&
    !pipelineStarted &&
    !genBusy &&
    Boolean(metaTitle.trim() || metaOwner.trim());
  const draftingEmpty =
    genBusy && metaLooksEmpty(reviewQuery.data?.meta ?? null);

  const scriptHelp =
    role === "gate"
      ? "此版本的剧本。启动流水线前请确认。PDF 或图片来源：使用提取工具，然后粘贴到此处或案件「源文」。"
      : "此版本的常备案件输入。可随时编辑。PDF 或图片来源：使用提取工具，然后粘贴到此处。";

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{resolvedTitle}</CardTitle>
          <CardDescription>{resolvedDescription}</CardDescription>
          <CardAction>
            <div className="flex flex-wrap items-center gap-2">
              {headerExtra}
              <Button
                type="button"
                variant="outline"
                disabled={busy || genBusy || !scriptEdit.trim()}
                onClick={() => void onGenerate()}
              >
                {genBusy ? <Spinner /> : null}
                {genBusy ? "正在草拟元信息…" : "重新草拟元信息"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy || genBusy}
                onClick={() => void onSave()}
              >
                {busy ? <Spinner /> : null}
                保存
              </Button>
            </div>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {crossLink ? (
            <p className="text-sm text-muted-foreground">{crossLink}</p>
          ) : null}
          {reviewQuery.isLoading || draftingEmpty ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-48 w-full" />
              {genBusy ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Spinner />
                  正在根据剧本草拟元信息…
                </p>
              ) : null}
            </div>
          ) : (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="source-script">原始剧本</FieldLabel>
                <Textarea
                  id="source-script"
                  value={scriptEdit}
                  onChange={(e) => setScriptEdit(e.target.value)}
                  rows={14}
                  className="max-h-80 overflow-y-auto font-mono text-xs"
                  disabled={fieldsLocked}
                  placeholder="粘贴完整案件剧本（markdown）…"
                />
                <FieldDescription>{scriptHelp}</FieldDescription>
              </Field>
              <div className="grid gap-4 md:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="source-id">案件 ID</FieldLabel>
                  <Input
                    id="source-id"
                    value={caseDisplayId}
                    readOnly
                    disabled
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-title">标题</FieldLabel>
                  <Input
                    id="source-title"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    disabled={fieldsLocked}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-owner">
                    主角（手机主人）
                  </FieldLabel>
                  <Input
                    id="source-owner"
                    value={metaOwner}
                    onChange={(e) => setMetaOwner(e.target.value)}
                    disabled={fieldsLocked}
                    placeholder="Sarah Miller"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-guide">向导</FieldLabel>
                  <Input
                    id="source-guide"
                    value={metaGuide}
                    onChange={(e) => setMetaGuide(e.target.value)}
                    disabled={fieldsLocked}
                    placeholder="Marcus Harris"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-player">玩家</FieldLabel>
                  <Input
                    id="source-player"
                    value={metaPlayer}
                    onChange={(e) => setMetaPlayer(e.target.value)}
                    disabled={fieldsLocked}
                    placeholder="Riley Park"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-tz">时区</FieldLabel>
                  <Combobox
                    items={tzItems}
                    value={metaTimezone || null}
                    onValueChange={(next) => {
                      if (typeof next === "string") setMetaTimezone(next);
                    }}
                    inputValue={metaTimezone}
                    onInputValueChange={(next) => setMetaTimezone(next)}
                    disabled={fieldsLocked}
                  >
                    <ComboboxInput
                      id="source-tz"
                      placeholder="America/Los_Angeles"
                      disabled={fieldsLocked}
                      showClear={false}
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>
                        无匹配项。请继续输入完整的 IANA 时区。
                      </ComboboxEmpty>
                      <ComboboxList>
                        {(item) => (
                          <ComboboxItem key={item} value={item}>
                            {item}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-tags">标签</FieldLabel>
                  <Input
                    id="source-tags"
                    value={metaTags}
                    onChange={(e) => setMetaTags(e.target.value)}
                    disabled={fieldsLocked}
                  />
                  <FieldDescription>逗号分隔。</FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-summary">摘要</FieldLabel>
                  <Textarea
                    id="source-summary"
                    value={metaSummary}
                    onChange={(e) => setMetaSummary(e.target.value)}
                    rows={3}
                    className="max-h-40 overflow-y-auto"
                    disabled={fieldsLocked}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="source-is-free">
                    免费谜题（is_free）
                  </FieldLabel>
                  <Input
                    id="source-is-free"
                    value={metaIsFree}
                    onChange={(e) => setMetaIsFree(e.target.value)}
                    disabled={fieldsLocked}
                    placeholder="3/10"
                  />
                  <FieldDescription>
                    格式为 免费数/总数（如 3/10）。分子 = 免费
                    puzzleSteps；分母 = 总 puzzleSteps。
                  </FieldDescription>
                </Field>
              </div>
              {pipelineStarted && showStartPipeline ? (
                <Alert>
                  <AlertTitle>流水线已启动</AlertTitle>
                  <AlertDescription>
                    剧本与元信息仍可在「源文」和此处编辑。若要再次运行流水线，请在「版本」页创建新版本。
                  </AlertDescription>
                </Alert>
              ) : null}
              {role === "source" && !pipelineStarted ? (
                <Alert>
                  <AlertTitle>在「元信息」页启动流水线</AlertTitle>
                  <AlertDescription>
                    「源文」用于持续编辑。准备启动流水线时，请打开此版本的「元信息」页。
                  </AlertDescription>
                </Alert>
              ) : null}
              {error ? <FieldError>{error}</FieldError> : null}
            </FieldGroup>
          )}
        </CardContent>
        <CardFooter className="justify-end border-t">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy || genBusy}
              onClick={() => void onSave()}
            >
              {busy ? <Spinner /> : null}
              保存
            </Button>
            {showStartPipeline && !pipelineStarted ? (
              <Button
                type="button"
                disabled={busy || !canStart}
                onClick={() => setConfirmOpen(true)}
              >
                {busy ? (
                  <Spinner />
                ) : (
                  <HugeiconsIcon icon={PlayIcon} data-icon="inline-start" />
                )}
                启动流水线
              </Button>
            ) : null}
          </div>
        </CardFooter>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>启动流水线？</AlertDialogTitle>
            <AlertDialogDescription>
              保存剧本 + 元信息，并启动 generate_phone → refine_phone → experience_guide →
              asset_plan → head_refs → gen_assets → review → enrich。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                void onStart();
              }}
            >
              <HugeiconsIcon icon={Tick02Icon} data-icon="inline-start" />
              启动
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
